import React, { useCallback, useEffect, useMemo, useState } from "react";
import "react-dates/initialize";
import "react-dates/lib/css/_datepicker.css";
import { DateRangePicker } from "react-dates";
import {
  FaCalendarAlt,
  FaChevronLeft,
  FaChevronRight,
  FaList,
  FaPhoneAlt,
  FaSearch,
  FaThLarge,
  FaUser,
} from "react-icons/fa";
import { useDispatch } from "react-redux";
import { setActivePage, setShowPreloader } from "../store/activePage";
import HandleGetEventList from "../hooks/handleGetEventList";
import formatCustomDateWithTime from "../helpers/customDateFormatWithTime";

function StatCard({ label, value, hint }) {
  return (
    <div className="rounded-2xl border border-white/70 bg-white/90 px-4 py-4 shadow-lg shadow-slate-200/50">
      <p className="text-xs font-bold uppercase tracking-[0.2em] text-teal-700">
        {label}
      </p>
      <p className="mt-2 text-2xl font-black text-slate-900">{value}</p>
      <p className="mt-1 text-xs text-slate-500">{hint}</p>
    </div>
  );
}

function EventStatusPill({ event }) {
  const isClosed = Boolean(event.result_file);

  return (
    <span
      className={`inline-flex items-center rounded-full border px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide ${
        isClosed
          ? "border-slate-300 bg-slate-100 text-slate-700"
          : "border-emerald-200 bg-emerald-50 text-emerald-700"
      }`}
    >
      {isClosed ? "Closed" : "Open"}
    </span>
  );
}

function EventListCard({ event }) {
  const location = [event.event_address, event.event_city, event.event_state]
    .filter(Boolean)
    .join(", ");

  return (
    <div className="rounded-3xl border border-white/70 bg-white/95 p-5 shadow-xl shadow-slate-200/50">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-xs font-bold uppercase tracking-[0.22em] text-teal-700">
            Event
          </p>
          <div className="mt-1 flex flex-wrap items-center gap-2">
            <h3 className="text-xl font-black text-slate-900">
              {event.event_type || "Untitled Event"}
            </h3>
            <span className="inline-flex items-center rounded-full border border-slate-200 bg-slate-100 px-2.5 py-1 text-[11px] font-bold text-slate-600">
              Event ID #{event.event_id}
            </span>
          </div>
          <p className="mt-2 text-sm font-semibold text-slate-500">
            {formatCustomDateWithTime(event.event_date)}
          </p>
        </div>
        <EventStatusPill event={event} />
      </div>

      <div className="mt-5 grid gap-3 sm:grid-cols-2">
        <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-slate-500">
            <FaUser className="text-slate-400" />
            Customer
          </div>
          <p className="mt-2 text-sm font-bold text-slate-900">
            {event.customer_name || "-"}
          </p>
        </div>

        <div className="rounded-2xl border border-teal-200 bg-teal-50 px-4 py-3">
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-teal-700">
            <FaPhoneAlt className="text-teal-600" />
            Customer Mobile
          </div>
          <p className="mt-2 text-sm font-black text-teal-900">
            {event.customer_mobile || "-"}
          </p>
        </div>
      </div>

      <div className="mt-4 grid gap-3 lg:grid-cols-2">
        <div className="rounded-2xl border border-slate-200 bg-white px-4 py-3">
          <p className="text-xs font-bold uppercase tracking-wide text-slate-500">
            Eventors
          </p>
          <p className="mt-2 text-sm font-semibold text-slate-800">
            {[event.eventor_1, event.eventor_2].filter(Boolean).join(", ") || "-"}
          </p>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white px-4 py-3">
          <p className="text-xs font-bold uppercase tracking-wide text-slate-500">
            Location
          </p>
          <p className="mt-2 text-sm font-semibold text-slate-800">
            {location || "-"}
          </p>
        </div>
      </div>
    </div>
  );
}

function EventListTable({ events }) {
  return (
    <div className="overflow-hidden rounded-3xl border border-white/70 bg-white/95 shadow-xl shadow-slate-200/50">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-slate-200">
          <thead className="bg-slate-50">
            <tr>
              {[
                "Event ID",
                "Event",
                "Date",
                "Customer",
                "Customer Mobile",
                "Eventors",
                "Status",
              ].map((label) => (
                <th
                  key={label}
                  className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wide text-slate-500"
                >
                  {label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {events.map((event) => (
              <tr key={event.event_id} className="hover:bg-slate-50/80">
                <td className="px-4 py-3 text-sm font-black text-slate-700">
                  #{event.event_id}
                </td>
                <td className="px-4 py-3 text-sm font-bold text-slate-900">
                  {event.event_type || "-"}
                </td>
                <td className="px-4 py-3 text-sm font-semibold text-slate-700">
                  {formatCustomDateWithTime(event.event_date)}
                </td>
                <td className="px-4 py-3 text-sm font-semibold text-slate-800">
                  {event.customer_name || "-"}
                </td>
                <td className="px-4 py-3 text-sm font-black text-teal-800">
                  {event.customer_mobile || "-"}
                </td>
                <td className="px-4 py-3 text-sm text-slate-700">
                  {[event.eventor_1, event.eventor_2].filter(Boolean).join(", ") || "-"}
                </td>
                <td className="px-4 py-3">
                  <EventStatusPill event={event} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const OperatorEventList = () => {
  const dispatch = useDispatch();
  const [events, setEvents] = useState([]);
  const [searchValue, setSearchValue] = useState("");
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [focusedInput, setFocusedInput] = useState(null);
  const [viewMode, setViewMode] = useState("grid");
  const [page, setPage] = useState(1);
  const [perPage] = useState(10);
  const [summary, setSummary] = useState({
    total: 0,
    open: 0,
    closed: 0,
    unique_customers: 0,
  });
  const [pagination, setPagination] = useState({
    page: 1,
    per_page: 10,
    total: 0,
    total_pages: 0,
  });
  const [debouncedSearchValue, setDebouncedSearchValue] = useState("");

  useEffect(() => {
    dispatch(
      setActivePage({
        headerName: "Organisation Event List",
        activeMenu: "eventList",
      }),
    );
  }, [dispatch]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedSearchValue(searchValue.trim());
      setPage(1);
    }, 350);

    return () => clearTimeout(timeoutId);
  }, [searchValue]);

  useEffect(() => {
    setPage(1);
  }, [startDate, endDate]);

  const fetchEvents = useCallback(async () => {
    dispatch(setShowPreloader(true));
    const response = await HandleGetEventList(
      startDate?.format("YYYY-MM-DD") || null,
      endDate?.format("YYYY-MM-DD") || null,
      debouncedSearchValue,
      "all",
      false,
      { page, perPage },
    );
    dispatch(setShowPreloader(false));
    setEvents(Array.isArray(response?.items) ? response.items : []);
    setSummary(
      response?.summary || {
        total: 0,
        open: 0,
        closed: 0,
        unique_customers: 0,
      },
    );
    setPagination(
      response?.pagination || {
        page,
        per_page: perPage,
        total: 0,
        total_pages: 0,
      },
    );
  }, [debouncedSearchValue, dispatch, endDate, page, perPage, startDate]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  const stats = useMemo(() => {
    return {
      total: summary.total || 0,
      open: summary.open || 0,
      closed: summary.closed || 0,
      uniqueCustomers: summary.unique_customers || 0,
    };
  }, [summary]);

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden p-6 lg:p-8">
    
      <div className="mt-6 grid shrink-0 gap-4 md:grid-cols-2 xl:grid-cols-4">
        <StatCard
          label="Total Events"
          value={stats.total}
          hint="Filtered total across all pages"
        />
        <StatCard
          label="Open Events"
          value={stats.open}
          hint="Filtered open events across all pages"
        />
        <StatCard
          label="Closed Events"
          value={stats.closed}
          hint="Filtered closed events across all pages"
        />
        <StatCard
          label="Customers"
          value={stats.uniqueCustomers}
          hint="Unique filtered customer records"
        />
      </div>

      <div className="sticky top-0 z-10 mt-6 shrink-0 rounded-3xl border border-white/70 bg-white/90 p-4 shadow-xl shadow-slate-200/50 backdrop-blur">
        <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_auto_auto]">
          <div className="relative">
            <FaSearch className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              placeholder="Search by customer name, customer mobile, eventors, or address"
              className="w-full rounded-2xl border border-slate-200 bg-slate-50 py-3 pl-11 pr-4 text-sm font-semibold text-slate-800 outline-none transition focus:border-teal-400 focus:bg-white"
            />
          </div>

          <div className="min-w-0 overflow-hidden rounded-2xl border border-slate-200 bg-slate-50 px-3 py-2">
            <DateRangePicker
              startDate={startDate}
              startDateId="operator-event-list-start-date"
              endDate={endDate}
              endDateId="operator-event-list-end-date"
              onDatesChange={({ startDate: nextStartDate, endDate: nextEndDate }) => {
                setStartDate(nextStartDate);
                setEndDate(nextEndDate);
              }}
              focusedInput={focusedInput}
              onFocusChange={setFocusedInput}
              minimumNights={0}
              isOutsideRange={() => false}
              displayFormat="DD/MM/YYYY"
              numberOfMonths={1}
              small
              hideKeyboardShortcutsPanel
              showClearDates
              startDatePlaceholderText="From date"
              endDatePlaceholderText="To date"
            />
          </div>

          <div className="flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={() => setViewMode("grid")}
              className={`inline-flex items-center gap-2 rounded-2xl px-4 py-3 text-sm font-bold transition ${
                viewMode === "grid"
                  ? "bg-teal-600 text-white shadow-lg shadow-teal-200"
                  : "border border-slate-200 bg-white text-slate-600"
              }`}
            >
              <FaThLarge />
              Cards
            </button>
            <button
              type="button"
              onClick={() => setViewMode("table")}
              className={`inline-flex items-center gap-2 rounded-2xl px-4 py-3 text-sm font-bold transition ${
                viewMode === "table"
                  ? "bg-teal-600 text-white shadow-lg shadow-teal-200"
                  : "border border-slate-200 bg-white text-slate-600"
              }`}
            >
              <FaList />
              Table
            </button>
          </div>
        </div>
      </div>

      <div className="mt-6 min-h-0 flex-1 overflow-hidden">
        <div className="h-full overflow-y-auto pr-1">
          {events.length === 0 ? (
            <div className="rounded-3xl border border-dashed border-slate-300 bg-white/85 px-6 py-16 text-center shadow-lg shadow-slate-200/40">
              <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-teal-100 text-teal-700">
                <FaCalendarAlt />
              </div>
              <h2 className="mt-4 text-xl font-black text-slate-900">
                No events found
              </h2>
              <p className="mt-2 text-sm text-slate-500">
                Try changing the date range or search text to see organisation events.
              </p>
            </div>
          ) : viewMode === "table" ? (
            <EventListTable events={events} />
          ) : (
            <div className="grid gap-5 xl:grid-cols-2">
              {events.map((event) => (
                <EventListCard key={event.event_id} event={event} />
              ))}
            </div>
          )}
        </div>
      </div>

      {pagination.total > 0 && (
        <div className="mt-6 shrink-0 flex flex-col gap-3 rounded-3xl border border-white/70 bg-white/90 px-5 py-4 shadow-xl shadow-slate-200/50 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="text-sm font-bold text-slate-900">
              Page {pagination.page} of {Math.max(pagination.total_pages || 1, 1)}
            </p>
            <p className="text-xs text-slate-500">
              Showing {Math.min((pagination.page - 1) * pagination.per_page + 1, pagination.total)}-
              {Math.min(pagination.page * pagination.per_page, pagination.total)} of {pagination.total} events
            </p>
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              disabled={pagination.page <= 1}
              onClick={() => setPage((current) => Math.max(current - 1, 1))}
              className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm font-bold text-slate-600 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
            >
              <FaChevronLeft />
              Previous
            </button>
            <button
              type="button"
              disabled={pagination.page >= pagination.total_pages}
              onClick={() =>
                setPage((current) =>
                  Math.min(current + 1, pagination.total_pages || current + 1),
                )
              }
              className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm font-bold text-slate-600 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
            >
              Next
              <FaChevronRight />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default OperatorEventList;
